/**
 * Freeze an EditGraph at publish time and build the full publish snapshot.
 */
import {
  resolveEdit,
  scoreEdit,
  serializeEditGraph,
  validateEditGraphAgainstPack,
  type EditGraph,
  type SourcePack,
} from "@desk/core";
import type { PackId } from "../content/registry.js";
import { landingConfigForPack } from "../content/landing/index.js";
import { commentsForPack } from "../content/levels/index.js";
import { commentsFromReactions } from "./comments.js";
import { buildSourceCompareReport } from "./compare.js";
import { crowdReactions } from "./emotion-model.js";
import {
  landingContextFromPresentation,
  landingEmotionVector,
} from "./audience-objective.js";
import { computePublishRewards } from "./rewards.js";
import type { PublishSnapshot } from "./types.js";
import type { GamePresentation } from "./challenge/presentation.js";

export function createPublishSnapshot(
  packId: PackId,
  pack: SourcePack,
  graph: EditGraph,
  presentation?: GamePresentation,
): PublishSnapshot {
  validateEditGraphAgainstPack(graph, pack);
  const frozen: EditGraph = JSON.parse(serializeEditGraph(graph)) as EditGraph;
  const resolved = resolveEdit(frozen, pack);
  const score = scoreEdit(pack, frozen, resolved);
  // Data-driven TOP COMMENTS (Job 5b): derive each comment from how each crowd
  // actually reacted to THIS edit (served crowd first), through the SAME landing
  // read the results screen uses. Presentation only; never feeds scoring.
  const config = landingConfigForPack(packId);
  const landing = landingContextFromPresentation(pack, presentation);
  const reactions = crowdReactions(landingEmotionVector(resolved, config, landing), config);
  return {
    packId,
    packVersion: pack.version,
    seed: frozen.seed,
    graphJson: serializeEditGraph(frozen),
    score,
    rewards: computePublishRewards(score),
    comments: commentsFromReactions(frozen.seed, reactions, commentsForPack(packId)),
    compare: buildSourceCompareReport(pack, frozen, resolved),
    ...(presentation ? { presentation } : {}),
  };
}
